import { useEffect, useState } from "react"
import { useParams, useRouteMatch } from "react-router-dom"

import { MDB_TYPES, SYSTEMS } from "@meddbriefer/scenario-data/constants"

import { useScenarioType } from "../ScenarioTypeContext"

import InterventionsEditorLayout from "./InterventionEditorLayout"

import DetailsEditor from "../DetailsEditor"
import InlineEditor from "../InlineEditor"
import AddEntity from "../AddEntity"
import AssociateEntity from "../AssociateEntity"

import { genInterventionPromptRec } from "./types"

import { getIntvsBC, getIntvBC } from "../urls"
import { swapUp, swapDown} from "../utils"

const sortRelatedRecsByForeignKeyFldOrder = (relatedRecs, sortedForeignKeyValues) => {
    const tmp = []
    sortedForeignKeyValues.forEach(id => {
        const found = relatedRecs.find(rec => rec.id === id)
        if (!!found) {
            tmp.push(found)
        } else {
            console.log(`sortRelatedRecsByForeignKeyFldOrder() id ${id} not found`)
            console.log(relatedRecs, sortedForeignKeyValues)
        }
    })
    return tmp
}

const EditIntervention = () => {
    const {
        scenarioType,
        intvs,
        intvPrompts,
        intvPromptAnswers,
        uniqueIDs,
        uniqueLabels,
    } = useScenarioType()
    const params = useParams()
    const {interventionID} = params
    const {url} = useRouteMatch()
    const [intv, setIntv] = useState(null)
    const [intvUpdated, setIntvUpdated] = useState(false)
    const [myDistPrompts, setMyDistPrompts] = useState([])
    const [distPromptsUpdated, setDistPromptsUpdated] = useState(false)
    const [myPrompts, setMyPrompts] = useState([])
    const [promptsUpdated, setPromptsUpdated] = useState(false)

    const updateIntvField = (entityID, fldName, newVal) => {
        setIntv(prevState => ({...prevState, [fldName]: newVal}))
        setIntvUpdated(true)
    }

    const moveDistPromptUp = (promptIdx) => {
        updateIntvField(intv.id, "distPrompts", swapUp(intv.distPrompts, promptIdx))
        setDistPromptsUpdated(true)
    }

    const moveDistPromptDown = (promptIdx) => {
        updateIntvField(intv.id, "distPrompts", swapDown(intv.distPrompts, promptIdx))
        setDistPromptsUpdated(true)
    }

    const movePromptUp = (promptIdx) => {
        updateIntvField(intv.id, "prompts", swapUp(intv.prompts, promptIdx))
        setPromptsUpdated(true)
    }

    const movePromptDown = (promptIdx) => {
        updateIntvField(intv.id, "prompts", swapDown(intv.prompts, promptIdx))
        setPromptsUpdated(true)
    }

    const associateDistPrompt = (entityType, _unused, associatedID) => {
        updateIntvField(intv.id, "distPrompts", intv.distPrompts.concat(associatedID))
        setDistPromptsUpdated(true)
    }

    const disassociateDistPrompt = (item) => {
        updateIntvField(intv.id, "distPrompts", intv.distPrompts.filter(id => id !== item.id))
        setDistPromptsUpdated(true)
    }

    const addPrompt = (entityType, newID, newLabel) => {
        intvPrompts.add(genInterventionPromptRec(newID, newLabel, intv.id))
        updateIntvField(intv.id, "prompts", intv.prompts.concat(newID))
        setPromptsUpdated(true)
    }

    const deletePrompt = (item) => {
        const myAnswers = scenarioType.interventionPromptAnswers.filter(e => e.promptID === item.id)
        let msg = `Are you sure you wish to delete the prompt "${item.label}" and it's children?`
        if (myAnswers.length > 0) {
            msg += "\nAnswers\n"
            msg += myAnswers.map(e => e.label).join("\n")
        }
        if (window.confirm(msg)) {
            myAnswers.forEach(e => intvPromptAnswers.remove(e.id))
            intvPrompts.remove(item.id)
            updateIntvField(intv.id, "prompts", intv.prompts.filter(id => id !== item.id))
            setPromptsUpdated(true)
        } else {
            console.log("deletion aborted")
        }
    }


    useEffect(
        () => {
            if (!!interventionID && !intv) {
                const found = scenarioType.interventions.find(e => e.id === interventionID)
                if (!!found) {
                    setIntv(found)
                    // I *think* we only want to setIntvUpdated on actual updates, not the initial load
                    // setIntvUpdated(true)
                    if (!intv) {
                        // set both to true to trigger loading of associated records
                        setDistPromptsUpdated(true)
                        setPromptsUpdated(true)
                    }
                } else {
                    alert(`intervention ${interventionID} lookup failed!`)
                }
            }
        }, [interventionID, intv, scenarioType.interventions]
    )

    useEffect(
        () => {
            if (distPromptsUpdated) {
                const matches = scenarioType.interventionPrompts.filter(e => intv.distPrompts.includes(e.id))
                setMyDistPrompts(sortRelatedRecsByForeignKeyFldOrder(matches, intv.distPrompts))
                setDistPromptsUpdated(false)
            }
            if (promptsUpdated) {
                let matches = scenarioType.interventionPrompts.filter(e => e.interventionID === intv.id)
                setMyPrompts(sortRelatedRecsByForeignKeyFldOrder(matches, intv.prompts))
                setPromptsUpdated(false)
            }
        }, [intv, distPromptsUpdated, promptsUpdated, scenarioType.interventionPrompts]
    )

    useEffect(
        () => {
            if (!!intv && intvUpdated) {
                intvs.update(intv)
                setIntvUpdated(false)
            }
        }, [intv, intvs, intvUpdated]
    )

    if (!intv) {
        return (<div>Loading...</div>)
    }
    return (
        <InterventionsEditorLayout
            breadcrumbs={[getIntvsBC(params), getIntvBC(params, intv.label)]}
        >
            <DetailsEditor
                heading="Intervention"
                entity={intv}
                fields={[{
                    name: "id",
                    label: "ID",
                    widget: null
                },{
                    name: "label",
                    label: "Label",
                    widget: "text",
                    changeHandler: updateIntvField,
                    unique: true,
                    uniqueValues: uniqueLabels,
                    records: scenarioType.interventions
                }, {
                    name: "abbrLabel",
                    label: "Abbr. Label",
                    widget: "text",
                    changeHandler: updateIntvField
                },
                {
                    name: "system",
                    label: "System",
                    widget: "select",
                    options: [
                        [SYSTEMS.AW, "Airway"],
                        [SYSTEMS.BR, "Breathing"],
                        [SYSTEMS.BL, "Bleeding"],
                        [SYSTEMS.SH, "Shock"],
                        [SYSTEMS.OTH, "Other"]
                    ],
                    changeHandler: updateIntvField
                }]}
            />

            <InlineEditor
                heading="Distinguishing Prompts"
                records={myDistPrompts}
                fields={[
                    { name: "id", label: "ID", colWidthPct: 40, widget: "smallText" },
                    { name: "label", label: "Label", colWidthPct: 60, widget: null }
                ]}
                allowChangeOrder={true}
                allowChangeIndent={false}
                onMoveUp={moveDistPromptUp}
                onMoveDown={moveDistPromptDown}
                editLink={false}
                allowDelete={true}
                onDelete={disassociateDistPrompt}
            >
                <AssociateEntity
                    entityDesc={{
                        type: MDB_TYPES.DIST_PROMPT,
                        name: "Distinguishing Prompt"
                    }}
                    choices={scenarioType.interventionPrompts.filter(e => e.type === MDB_TYPES.DIST_PROMPT)}
                    addHandler={associateDistPrompt}
                    uniqueIDs={uniqueIDs}
                />

            </InlineEditor>

            <InlineEditor
                heading="Prompts"
                records={myPrompts}
                fields={[
                    { name: "id", label: "ID", colWidthPct: 40, widget: "smallText" },
                    { name: "label", label: "Label", colWidthPct: 60, widget: null }
                ]}
                allowChangeOrder={true}
                allowChangeIndent={false}
                onMoveUp={movePromptUp}
                onMoveDown={movePromptDown}
                editLink={true}
                editBaseURL={`${url}/prompt`}
                allowDelete={true}
                onDelete={deletePrompt}
            >
                <AddEntity
                    entitiesDesc={[{type: MDB_TYPES.PROMPT, name: "Prompt", labelsUnique: true}]}
                    addHandler={addPrompt}
                    uniqueIDs={uniqueIDs}
                    uniqueLabels={uniqueLabels}
                />
            </InlineEditor>
        </InterventionsEditorLayout>
    )
}

export default EditIntervention